/**
 * Chiptune audio: pulse / triangle / noise voices, a lookahead sequencer for looping scene
 * music, and enveloped sound effects. No assets — everything is synthesised with Web Audio.
 */

type Wave = "p12" | "p25" | "p50" | "tri" | "saw" | "noise";

interface Chan {
  wave: Wave;
  vol: number;
  /** Bars of 16 steps each, tokens: note (C#5), sustain (-), rest (.), drums k/s/h. */
  bars: string[];
  /** Release time in seconds. */
  rel?: number;
  vib?: boolean;
}

interface Song {
  bpm: number;
  loop: boolean;
  chans: Chan[];
}

const NOTE: Record<string, number> = { C: -9, D: -7, E: -5, F: -4, G: -2, A: 0, B: 2 };
function freqOf(tok: string): number {
  const m = /^([A-G])([#b]?)(\d)$/.exec(tok);
  if (!m) return 0;
  const semi = NOTE[m[1]!]! + (m[2] === "#" ? 1 : m[2] === "b" ? -1 : 0) + (Number(m[3]) - 4) * 12;
  return 440 * Math.pow(2, semi / 12);
}

const rep = (s: string, n: number) => Array<string>(n).fill(s);

const SONGS: Record<string, Song> = {
  title: {
    bpm: 84,
    loop: true,
    chans: [
      { wave: "p25", vol: 0.11, vib: true, rel: 0.25, bars: [
        "E5 - - - G5 - - - A5 - G5 - E5 - - -",
        "D5 - - - G5 - - - B5 - A5 - G5 - - -",
        "C5 - - - E5 - - - A5 - B5 - C6 - - -",
        "A5 - - - G5 - F5 - E5 - - - . . . .",
        "E5 - - - G5 - - - C6 - B5 - G5 - - -",
        "D5 - - - B4 - - - D5 - G5 - A5 - - -",
        "C6 - - - B5 - A5 - E5 - - - A5 - - -",
        "G5 - - - F5 - E5 - D5 - - - . . . .",
      ] },
      { wave: "p12", vol: 0.045, rel: 0.12, bars: [
        "C4 E4 G4 C5 E5 C5 G4 E4 C4 E4 G4 C5 E5 C5 G4 E4",
        "B3 D4 G4 B4 D5 B4 G4 D4 B3 D4 G4 B4 D5 B4 G4 D4",
        "C4 E4 A4 C5 E5 C5 A4 E4 C4 E4 A4 C5 E5 C5 A4 E4",
        "C4 F4 A4 C5 F5 C5 A4 F4 C4 F4 A4 C5 F5 C5 A4 F4",
      ] },
      { wave: "tri", vol: 0.16, rel: 0.2, bars: [
        "C3 - - - - - - - G2 - - - - - - -",
        "G2 - - - - - - - D3 - - - - - - -",
        "A2 - - - - - - - E3 - - - - - - -",
        "F2 - - - - - - - C3 - - - - - - -",
      ] },
    ],
  },
  town: {
    bpm: 108,
    loop: true,
    chans: [
      { wave: "p25", vol: 0.1, rel: 0.12, bars: [
        "C5 - A4 - C5 - F5 - - - E5 - D5 - C5 -",
        "D5 - - - A4 - - - D5 - E5 - F5 - - -",
        "F5 - D5 - Bb4 - D5 - F5 - G5 - F5 - D5 -",
        "E5 - - - C5 - D5 - E5 - G5 - - - . .",
        "A5 - - - G5 - F5 - C5 - - - F5 - A5 -",
        "G5 - F5 - E5 - D5 - A4 - - - D5 - - -",
        "D5 - F5 - Bb5 - A5 - G5 - F5 - D5 - - -",
        "C5 - E5 - G5 - C6 - - - . . . . . .",
      ] },
      { wave: "p12", vol: 0.05, rel: 0.06, bars: [
        ". . A4 . . . C5 . . . A4 . . . C5 .",
        ". . F4 . . . A4 . . . F4 . . . A4 .",
        ". . D4 . . . F4 . . . D4 . . . F4 .",
        ". . E4 . . . G4 . . . E4 . . . G4 .",
      ] },
      { wave: "tri", vol: 0.17, rel: 0.1, bars: [
        "F2 . . . C3 . . . F2 . . . C3 . . .",
        "D3 . . . A2 . . . D3 . . . A2 . . .",
        "Bb2 . . . F2 . . . Bb2 . . . F2 . . .",
        "C3 . . . G2 . . . C3 . . . E3 . . .",
      ] },
      { wave: "noise", vol: 0.03, bars: [". . h . . . h . . . h . . . h ."] },
    ],
  },
  route: {
    bpm: 132,
    loop: true,
    chans: [
      { wave: "p25", vol: 0.1, vib: true, rel: 0.1, bars: [
        "D5 - - - G5 - - - F#5 - G5 - A5 - B5 -",
        "G5 - - - E5 - - - B4 - - - E5 - G5 -",
        "E5 - - - C5 - - - G5 - - - E5 - C5 -",
        "D5 - - - - - - - F#5 - A5 - D6 - - -",
        "B5 - - - A5 - G5 - D5 - - - G5 - B5 -",
        "A5 - G5 - E5 - - - G5 - - - B5 - - -",
        "C6 - B5 - A5 - G5 - E5 - G5 - C6 - - -",
        "B5 - A5 - F#5 - - - D5 - - - . . . .",
      ] },
      { wave: "p12", vol: 0.04, rel: 0.05, bars: [
        "G4 B4 D5 B4 G4 B4 D5 B4 G4 B4 D5 B4 G4 B4 D5 B4",
        "E4 G4 B4 G4 E4 G4 B4 G4 E4 G4 B4 G4 E4 G4 B4 G4",
        "C4 E4 G4 E4 C4 E4 G4 E4 C4 E4 G4 E4 C4 E4 G4 E4",
        "D4 F#4 A4 F#4 D4 F#4 A4 F#4 D4 F#4 A4 F#4 D4 F#4 A4 F#4",
      ] },
      { wave: "tri", vol: 0.17, rel: 0.06, bars: [
        "G2 . G3 . G2 . G3 . G2 . G3 . D3 . G3 .",
        "E2 . E3 . E2 . E3 . E2 . E3 . B2 . E3 .",
        "C3 . C4 . C3 . C4 . C3 . C4 . G2 . C4 .",
        "D3 . D4 . D3 . D4 . A2 . D4 . F#3 . A3 .",
      ] },
      { wave: "noise", vol: 0.045, bars: ["k . h . s . h . k . h . s . h h"] },
    ],
  },
  battle: {
    bpm: 156,
    loop: true,
    chans: [
      { wave: "p25", vol: 0.1, rel: 0.08, bars: [
        "A5 - - - E5 - - - A5 - B5 - C6 - B5 -",
        "A5 - - - F5 - - - C6 - - - A5 - - -",
        "B5 - - - G5 - - - D6 - C6 - B5 - G5 -",
        "G#5 - - - E5 - - - B5 - - - G#5 - - -",
        "C6 - B5 - A5 - E5 - A5 - C6 - E6 - - -",
        "D6 - C6 - A5 - F5 - A5 - - - C6 - - -",
        "B5 - A5 - G5 - D5 - G5 - B5 - D6 - - -",
        "E6 - - - D6 - - - B5 - - - G#5 - - -",
      ] },
      { wave: "p12", vol: 0.04, rel: 0.04, bars: [
        "A4 C5 E5 A4 C5 E5 A4 C5 E5 A4 C5 E5 A4 C5 E5 C5",
        "A4 C5 F5 A4 C5 F5 A4 C5 F5 A4 C5 F5 A4 C5 F5 C5",
        "B4 D5 G5 B4 D5 G5 B4 D5 G5 B4 D5 G5 B4 D5 G5 D5",
        "B4 E5 G#5 B4 E5 G#5 B4 E5 G#5 B4 E5 G#5 B4 E5 G#5 E5",
      ] },
      { wave: "tri", vol: 0.18, rel: 0.04, bars: [
        rep("A2 A3", 8).join(" "),
        rep("F2 F3", 8).join(" "),
        rep("G2 G3", 8).join(" "),
        rep("E2 E3", 8).join(" "),
      ] },
      { wave: "noise", vol: 0.05, bars: ["k . h . s . h . k k h . s . h s"] },
    ],
  },
  victory: {
    bpm: 150,
    loop: false,
    chans: [
      { wave: "p25", vol: 0.12, rel: 0.2, bars: ["G5 - G5 - G5 - E5 - G5 - - - C6 - - -", "B5 - A5 - G5 - E5 - C6 - - - - - - -"] },
      { wave: "p12", vol: 0.06, rel: 0.2, bars: ["E5 - E5 - E5 - C5 - E5 - - - G5 - - -", "D5 - C5 - B4 - G4 - E5 - - - - - - -"] },
      { wave: "tri", vol: 0.18, rel: 0.2, bars: ["C3 - - - - - - - C3 - - - - - - -", "G2 - - - - - - - C3 - - - - - - -"] },
    ],
  },
};

interface Ev { f: number; len: number; drum?: string }

function compile(ch: Chan): (Ev | null)[] {
  const toks = ch.bars.join(" ").trim().split(/\s+/);
  const out: (Ev | null)[] = toks.map(() => null);
  let last: Ev | null = null;
  toks.forEach((t, i) => {
    if (t === "-") { if (last) last.len++; return; }
    if (t === ".") { last = null; return; }
    if (t === "k" || t === "s" || t === "h") { out[i] = { f: 0, len: 1, drum: t }; last = null; return; }
    const ev: Ev = { f: freqOf(t), len: 1 };
    out[i] = ev;
    last = ev;
  });
  return out;
}

export class AudioBus {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private musicBus: GainNode | null = null;
  private sfxBus: GainNode | null = null;
  private waves = new Map<Wave, PeriodicWave>();
  private noiseBuf: AudioBuffer | null = null;
  private song: string | null = null;
  private wanted: string | null = null;
  private compiled: (Ev | null)[][] = [];
  private stepIdx = 0;
  private nextTime = 0;
  private timer: ReturnType<typeof setInterval> | null = null;
  private _muted = false;

  get muted(): boolean {
    return this._muted;
  }

  set muted(v: boolean) {
    this._muted = v;
    if (this.master && this.ctx) this.master.gain.setTargetAtTime(v ? 0 : 1, this.ctx.currentTime, 0.02);
  }

  /** Create the context on the first user gesture (browser autoplay policy). */
  unlock(): void {
    const ctx = this.ac();
    if (ctx && this.wanted && this.song !== this.wanted) this.startSong(this.wanted);
  }

  private ac(): AudioContext | null {
    if (!this.ctx) {
      if (typeof window === "undefined") return null;
      const Ctx = window.AudioContext || (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctx) return null;
      this.ctx = new Ctx();
      this.master = this.ctx.createGain();
      this.master.gain.value = this._muted ? 0 : 1;
      const comp = this.ctx.createDynamicsCompressor();
      comp.threshold.value = -14;
      comp.ratio.value = 3;
      this.master.connect(comp).connect(this.ctx.destination);
      this.musicBus = this.ctx.createGain();
      this.musicBus.gain.value = 0.55;
      this.musicBus.connect(this.master);
      this.sfxBus = this.ctx.createGain();
      this.sfxBus.gain.value = 0.9;
      this.sfxBus.connect(this.master);
      document.addEventListener?.("visibilitychange", () => {
        if (!this.ctx) return;
        if (document.hidden) void this.ctx.suspend();
        else void this.ctx.resume();
      });
    }
    if (this.ctx.state === "suspended" && !document.hidden) void this.ctx.resume();
    return this.ctx;
  }

  private wave(w: Wave): PeriodicWave | null {
    const ctx = this.ctx;
    if (!ctx || w === "tri" || w === "saw" || w === "noise") return null;
    let hit = this.waves.get(w);
    if (!hit) {
      const duty = w === "p12" ? 0.125 : w === "p25" ? 0.25 : 0.5;
      const n = 32;
      const real = new Float32Array(n), imag = new Float32Array(n);
      for (let k = 1; k < n; k++) {
        real[k] = Math.sin(2 * Math.PI * k * duty) / (k * Math.PI);
        imag[k] = (1 - Math.cos(2 * Math.PI * k * duty)) / (k * Math.PI);
      }
      hit = ctx.createPeriodicWave(real, imag);
      this.waves.set(w, hit);
    }
    return hit;
  }

  private noise(): AudioBuffer | null {
    if (!this.ctx) return null;
    if (!this.noiseBuf) {
      const len = this.ctx.sampleRate;
      this.noiseBuf = this.ctx.createBuffer(1, len, this.ctx.sampleRate);
      const d = this.noiseBuf.getChannelData(0);
      for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
    }
    return this.noiseBuf;
  }

  /** One enveloped oscillator voice. */
  private voice(bus: GainNode, w: Wave, f: number, t: number, dur: number, vol: number, o: { slide?: number; rel?: number; vib?: boolean; attack?: number } = {}): void {
    const ctx = this.ctx!;
    const osc = ctx.createOscillator();
    const pw = this.wave(w);
    if (pw) osc.setPeriodicWave(pw);
    else osc.type = w === "tri" ? "triangle" : "sawtooth";
    osc.frequency.setValueAtTime(f, t);
    if (o.slide) osc.frequency.exponentialRampToValueAtTime(Math.max(20, o.slide), t + dur);
    if (o.vib && dur > 0.2) {
      const lfo = ctx.createOscillator();
      const depth = ctx.createGain();
      lfo.frequency.value = 5.5;
      depth.gain.setValueAtTime(0, t);
      depth.gain.linearRampToValueAtTime(f * 0.012, t + Math.min(0.3, dur));
      lfo.connect(depth).connect(osc.frequency);
      lfo.start(t);
      lfo.stop(t + dur + (o.rel ?? 0.1));
    }
    const g = ctx.createGain();
    const a = o.attack ?? 0.005;
    const rel = o.rel ?? 0.08;
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(vol, t + a);
    g.gain.setValueAtTime(vol, t + Math.max(a, dur - 0.01));
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur + rel);
    osc.connect(g).connect(bus);
    osc.start(t);
    osc.stop(t + dur + rel + 0.02);
  }

  private hitNoise(bus: GainNode, t: number, dur: number, vol: number, freq: number, type: BiquadFilterType = "bandpass"): void {
    const ctx = this.ctx!;
    const buf = this.noise();
    if (!buf) return;
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const flt = ctx.createBiquadFilter();
    flt.type = type;
    flt.frequency.value = freq;
    flt.Q.value = type === "bandpass" ? 0.9 : 0.7;
    const g = ctx.createGain();
    g.gain.setValueAtTime(vol, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    src.connect(flt).connect(g).connect(bus);
    src.start(t, Math.random() * 0.5);
    src.stop(t + dur + 0.02);
  }

  // ---- Music -------------------------------------------------------------------------------

  /** Switch the looping track (no-op when already playing it). */
  music(name: string | null): void {
    if (this.song && SONGS[this.song] && !SONGS[this.song]!.loop) {
      // Let a jingle finish; resume with the requested loop afterwards.
      this.wantedAfter = name;
      return;
    }
    this.wanted = name;
    if (name === this.song) return;
    if (!this.ctx) return; // starts on unlock()
    this.startSong(name);
  }

  private startSong(name: string | null): void {
    const ctx = this.ctx;
    if (!ctx || !this.musicBus) return;
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    // Quick fade between tracks.
    this.musicBus.gain.cancelScheduledValues(ctx.currentTime);
    this.musicBus.gain.setValueAtTime(0.55, ctx.currentTime);
    this.song = name;
    if (!name || !SONGS[name]) return;
    this.compiled = SONGS[name]!.chans.map(compile);
    this.stepIdx = 0;
    this.nextTime = ctx.currentTime + 0.06;
    this.timer = setInterval(() => this.schedule(), 25);
    this.schedule();
  }

  private schedule(): void {
    const ctx = this.ctx;
    const song = this.song ? SONGS[this.song] : null;
    if (!ctx || !song || !this.musicBus) return;
    const stepDur = 60 / song.bpm / 4;
    const total = Math.max(...this.compiled.map((c) => c.length));
    while (this.nextTime < ctx.currentTime + 0.15) {
      if (!song.loop && this.stepIdx >= total) {
        if (this.timer) clearInterval(this.timer);
        this.timer = null;
        this.song = null;
        const back = this.wantedAfter;
        this.wantedAfter = null;
        if (back) this.music(back);
        return;
      }
      song.chans.forEach((ch, ci) => {
        const seq = this.compiled[ci]!;
        const ev = seq[this.stepIdx % seq.length];
        if (!ev) return;
        if (ev.drum) {
          if (ev.drum === "k") { this.voice(this.musicBus!, "tri", 140, this.nextTime, 0.08, ch.vol * 3.2, { slide: 45, rel: 0.05 }); }
          else if (ev.drum === "s") this.hitNoise(this.musicBus!, this.nextTime, 0.12, ch.vol * 2.2, 1800);
          else this.hitNoise(this.musicBus!, this.nextTime, 0.04, ch.vol * 1.4, 7000, "highpass");
          return;
        }
        this.voice(this.musicBus!, ch.wave, ev.f, this.nextTime, ev.len * stepDur * 0.92, ch.vol, { rel: ch.rel, vib: ch.vib });
      });
      this.nextTime += stepDur;
      this.stepIdx++;
    }
  }

  private wantedAfter: string | null = null;

  /** Play a one-shot jingle, then resume `after`. */
  jingle(name: string, after: string | null): void {
    this.wantedAfter = after;
    this.wanted = name;
    if (!this.ctx) return;
    this.startSong(name);
  }

  // ---- Sound effects -----------------------------------------------------------------------

  private sfx(fn: (bus: GainNode, t: number) => void): void {
    const ctx = this.ac();
    if (!ctx || !this.sfxBus || this._muted) return;
    fn(this.sfxBus, ctx.currentTime + 0.005);
  }

  confirm(): void {
    this.sfx((b, t) => { this.voice(b, "p25", 880, t, 0.04, 0.09); this.voice(b, "p25", 1320, t + 0.045, 0.06, 0.08); });
  }

  back(): void {
    this.sfx((b, t) => { this.voice(b, "p25", 520, t, 0.05, 0.08, { slide: 330 }); });
  }

  cursor(): void {
    this.sfx((b, t) => { this.voice(b, "p12", 1560, t, 0.018, 0.05, { rel: 0.02 }); });
  }

  hit(): void {
    this.sfx((b, t) => {
      this.hitNoise(b, t, 0.16, 0.35, 900);
      this.voice(b, "tri", 180, t, 0.12, 0.3, { slide: 60 });
    });
  }

  crit(): void {
    this.sfx((b, t) => {
      this.hitNoise(b, t, 0.22, 0.4, 1400);
      this.voice(b, "p25", 1760, t, 0.05, 0.1);
      this.voice(b, "p25", 2640, t + 0.05, 0.08, 0.09);
      this.voice(b, "tri", 200, t, 0.16, 0.32, { slide: 50 });
    });
  }

  /** A short layer over the hit sound that tells attack styles apart. */
  flourish(style: string): void {
    this.sfx((b, t) => {
      switch (style) {
        case "slash": this.hitNoise(b, t, 0.08, 0.18, 3200, "highpass"); this.voice(b, "p12", 1400, t, 0.06, 0.05, { slide: 700 }); break;
        case "slam": this.voice(b, "tri", 110, t, 0.22, 0.34, { slide: 38 }); this.hitNoise(b, t, 0.2, 0.22, 260, "lowpass"); break;
        case "flurry": [0, 0.05, 0.1].forEach((d) => this.hitNoise(b, t + d, 0.03, 0.14, 1600)); break;
        case "rain": [0, 0.03, 0.07, 0.11, 0.16].forEach((d, i) => this.hitNoise(b, t + d, 0.02, 0.08, 2200 + i * 300, "highpass")); break;
        case "wave": this.voice(b, "p50", 440, t, 0.18, 0.05, { slide: 660, vib: true }); break;
        case "vortex": this.voice(b, "saw", 260, t, 0.24, 0.035, { slide: 900 }); break;
        case "beam": this.voice(b, "p25", 1800, t, 0.14, 0.05, { slide: 420 }); break;
        case "siphon": [392, 523, 659].forEach((f, i) => this.voice(b, "p25", f, t + 0.08 + i * 0.05, 0.05, 0.05)); break;
        case "volley": case "orb": this.voice(b, "p12", 900, t, 0.05, 0.05, { slide: 520 }); break;
        case "shield": this.voice(b, "p25", 1046, t, 0.08, 0.06, { rel: 0.2 }); this.voice(b, "p25", 1568, t + 0.06, 0.1, 0.05, { rel: 0.25 }); break;
        case "boost": this.voice(b, "p25", 523, t, 0.16, 0.06, { slide: 1046 }); break;
        case "hex": this.voice(b, "p50", 660, t, 0.16, 0.05, { slide: 330 }); break;
      }
    });
  }

  heal(): void {
    this.sfx((b, t) => {
      [523, 659, 784, 1046].forEach((f, i) => this.voice(b, "p25", f, t + i * 0.06, 0.09, 0.07, { rel: 0.15 }));
    });
  }

  catchShake(): void {
    this.sfx((b, t) => { this.hitNoise(b, t, 0.06, 0.25, 600); this.voice(b, "tri", 220, t, 0.05, 0.2, { slide: 120 }); });
  }

  catchOk(): void {
    this.sfx((b, t) => {
      [784, 988, 1175, 1568].forEach((f, i) => this.voice(b, "p25", f, t + i * 0.08, 0.1, 0.09, { rel: 0.25 }));
    });
  }

  fail(): void {
    this.sfx((b, t) => { this.voice(b, "p50", 330, t, 0.25, 0.08, { slide: 110 }); });
  }

  step(): void {
    this.sfx((b, t) => { this.hitNoise(b, t, 0.03, 0.05, 2500); });
  }

  encounter(): void {
    this.sfx((b, t) => {
      [440, 554, 659, 880, 659, 880].forEach((f, i) => this.voice(b, "p25", f, t + i * 0.05, 0.045, 0.09));
    });
  }

  buy(): void {
    this.sfx((b, t) => { this.voice(b, "p25", 1976, t, 0.05, 0.08); this.voice(b, "p25", 2637, t + 0.06, 0.18, 0.08, { rel: 0.2 }); });
  }

  levelUp(): void {
    this.sfx((b, t) => {
      [523, 659, 784, 1046, 1318].forEach((f, i) => this.voice(b, "p25", f, t + i * 0.07, 0.07, 0.08, { rel: 0.2 }));
    });
  }

  toss(): void {
    this.sfx((b, t) => { this.voice(b, "p12", 400, t, 0.3, 0.06, { slide: 1400 }); });
  }
}
